import { useEffect, useState } from "react";
import { tryApiGet } from "../api/client";
import { colorFor } from '../lib/definitions/palette';
import { packetCategoryInfo, PROTOCOL_LABEL, isProtocol, meshcoreTruncationTag } from '../lib/definitions/protocols';
import { routeProvenanceLabel } from '../lib/definitions/map';
import { trustTierInfo } from '../lib/definitions/trust';
import { relativeTime } from "../lib/format";
import { sourceChipText } from "../lib/packetFeed";
import { useAppData } from "../state/AppData";
import { ObserverBadge } from "./ObserverBadge";
import { PacketBytesSection } from "./PacketBytesSection";
import { PayloadView } from "./PayloadView";
import { forPacketPath, offersTrace, traceLinkHash, type TraceOfPacket } from "../lib/traces";
import { navigate } from "../lib/router";
import type { Packet, PacketCoverageResponse } from "../api/types";

// Coverage (phase 3 backend, wired 2026-09-20): every observer that
// reported this exact content-hash-deduped packet — richer than this
// panel's own single "Observer" row above, which only ever shows the one
// observer this particular Packet record came from. Fetched lazily per
// packet (this panel only ever shows one packet at a time) rather than
// batch-loaded for the whole feed.
function PacketCoverageSection({ hash }: { hash: string }) {
  const [coverage, setCoverage] = useState<PacketCoverageResponse | null | "unavailable">(null);

  useEffect(() => {
    let cancelled = false;
    setCoverage(null);
    tryApiGet<PacketCoverageResponse>(`/api/packets/${encodeURIComponent(hash)}/coverage`)
      .then((res) => !cancelled && setCoverage(res ?? "unavailable"))
      .catch(() => !cancelled && setCoverage("unavailable"));
    return () => {
      cancelled = true;
    };
  }, [hash]);

  if (coverage === null) return <div className="node-detail-note">Loading…</div>;
  if (coverage === "unavailable") return <div className="node-detail-note">Coverage isn't available for this packet right now.</div>;

  return (
    <div>
      <ul className="observer-sighting-list">
        {coverage.observations.map((o, i) => (
          <li key={`${o.observer_id}-${i}`}>
            <ObserverBadge observerId={o.observer_id} label={o.observer_label} trustTier={o.trust_tier} />
            <span className="observer-sighting-count">
              {o.snr != null ? `${o.snr}dB` : "--"} / {o.rssi != null ? `${o.rssi}dBm` : "--"}
            </span>
            <span className="observer-sighting-count">{o.path}</span>
            <span className="observer-sighting-count">{relativeTime(o.observed_at)}</span>
          </li>
        ))}
        {coverage.observations.length === 0 && !coverage.corescope && <li className="empty-state">No observers recorded for this packet.</li>}
      </ul>
      {coverage.corescope && (
        <div style={{ marginTop: 6 }}>
          <div className="k" style={{ color: "var(--ink-muted)", marginBottom: 2 }}>
            CoreScope · {coverage.corescope.reception_count} {coverage.corescope.reception_count === 1 ? "reception" : "receptions"}
          </div>
          <ul className="observer-sighting-list">
            {coverage.corescope.receptions.map((r, i) => (
              <li key={`${r.station_id}-${i}`}>
                <span>{r.station || "—"}</span>
                <span className="observer-sighting-count">
                  {r.snr != null ? `${r.snr} dB` : "—"} / {r.rssi != null ? `${r.rssi} dBm` : "—"}
                </span>
                <span className="observer-sighting-count">{r.observed_at ? relativeTime(r.observed_at) : "—"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {coverage.mqtt_gateways.length > 0 && (
        <div className="node-detail-note" style={{ marginTop: 6 }}>
          Heard via MQTT gateway {coverage.mqtt_gateways.map((g) => `${g.gateway_id} (channel ${g.channel_name}, ${g.decrypt_state})`).join("; ")}.
        </div>
      )}
    </div>
  );
}

// A TRACE packet's own link to the Traces tab, opened on its tag. Shown only once the server has read the tag and a window
// reaches the packet (older ones are no longer listed).
function ShowTraceLink({ packet }: { packet: Packet }) {
  const { select } = useAppData();
  const [found, setFound] = useState<TraceOfPacket | null>(null);

  useEffect(() => {
    let cancelled = false;
    setFound(null);
    tryApiGet<TraceOfPacket>(forPacketPath(packet.id))
      .then((res) => !cancelled && setFound(res))
      .catch(() => !cancelled && setFound(null));
    return () => {
      cancelled = true;
    };
  }, [packet.id]);

  const target = traceLinkHash(found);
  if (!target) return null;
  return (
    <button
      type="button"
      className="sign-out-btn tr-link"
      onClick={() => {
        select(null);
        navigate(target);
      }}
    >
      Show this trace
    </button>
  );
}

export function PacketDetailPanel({ packet, onClose }: { packet: Packet; onClose: () => void }) {
  const { nodes, effectiveTheme, select, replaySinglePacket } = useAppData();
  const cat = packetCategoryInfo(packet.protocol, packet.type);
  const fromNode = packet.from_id != null ? nodes.get(String(packet.from_id)) : undefined;
  const toNode = packet.to_id != null ? nodes.get(String(packet.to_id)) : undefined;
  const protocolLabel = isProtocol(packet.protocol) ? PROTOCOL_LABEL[packet.protocol] : packet.protocol;

  // Route-provenance line (live-map spec §4.3 — audited 2026-09-20, see
  // routeProvenanceLabel's own comment): a MeshCore packet with real
  // carried route_hops states that plainly; anything else states the
  // hop-consumption fact it actually has (Meshtastic's hop_start/hop_limit)
  // without implying an inferred path, since no inference pipeline exists
  // server-side yet — that's a backend gap, flagged, not faked here.
  const routeKnown = routeProvenanceLabel(packet.protocol, packet.route_hops);
  const hopFact =
    packet.hop_start != null && packet.hop_limit != null
      ? `${packet.hop_start - packet.hop_limit} of ${packet.hop_start} hops consumed — no inferred path available for this protocol yet`
      : "No hop or route data available for this packet";
  const provenance = routeKnown ?? hopFact;
  // MeshCore-truncation task (2026-09-29): a companion-link capture at the
  // on-air ceiling may be missing its own payload tail — flagged here
  // until/unless a matching CoreScope capture recovers it. Meshtastic
  // packets never carry either key, so this is null for them.
  const truncationTag = meshcoreTruncationTag(packet.protocol_extra);
  // A MeshCore TRACE packet has no hop budget or inferred path of its own; its route is in the trace (Packets > Traces).
  const isTrace = offersTrace(packet);

  return (
    <div className="detail-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <h3>
          <span
            className="badge"
            style={{ background: colorFor(cat.color, effectiveTheme) }}
          >
            {cat.label}
          </span>
          {truncationTag && (
            <span
              className="badge"
              style={{ background: colorFor(truncationTag.color, effectiveTheme), marginLeft: 6 }}
            >
              {truncationTag.label}
            </span>
          )}
        </h3>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>

      <div className="detail-row">
        <span className="k">Type</span>
        <span>{packet.type}</span>
      </div>
      <div className="detail-row">
        <span className="k">Time</span>
        <span>{relativeTime(packet.ts)}</span>
      </div>
      <div className="detail-row">
        <span className="k">From</span>
        <span
          style={fromNode ? { cursor: "pointer", textDecoration: "underline" } : undefined}
          onClick={() => fromNode && select({ kind: "node", id: String(fromNode.id) })}
        >
          {fromNode?.short_name || fromNode?.long_name || packet.sender_name || String(packet.from_id ?? "unknown")}
        </span>
      </div>
      <div className="detail-row">
        <span className="k">To</span>
        <span
          style={toNode ? { cursor: "pointer", textDecoration: "underline" } : undefined}
          onClick={() => toNode && select({ kind: "node", id: String(toNode.id) })}
        >
          {toNode?.short_name || toNode?.long_name || String(packet.to_id ?? "broadcast")}
        </span>
      </div>
      <div className="detail-row">
        <span className="k">SNR / RSSI</span>
        <span>
          {packet.snr ?? "--"}dB / {packet.rssi ?? "--"}dBm
        </span>
      </div>
      {!isTrace && (
        <div className="detail-row">
          <span className="k">Hops</span>
          <span>
            {packet.hop_start ?? "--"} start / {packet.hop_limit ?? "--"} remaining
          </span>
        </div>
      )}
      <div className="detail-row">
        <span className="k">Channel</span>
        <span>{packet.channel ?? "--"}</span>
      </div>
      {/* relay_node/path are MeshCore-relevant and often absent on
          Meshtastic — omitted entirely rather than printed as "--" when
          not present, per the Packets-page brief. */}
      {packet.relay_node != null && (
        <div className="detail-row">
          <span className="k">Relay node</span>
          <span>{packet.relay_node}</span>
        </div>
      )}
      {packet.path && !isTrace && (
        <div className="detail-row">
          <span className="k">Path</span>
          <span>{packet.path}</span>
        </div>
      )}
      {packet.sources && packet.sources.length > 0 && (
        <div className="detail-row">
          <span className="k">Source</span>
          <span>
            {packet.sources.map((src, i) => (
              <div key={`${src.kind}-${src.label}-${i}`}>{sourceChipText(src)}</div>
            ))}
          </span>
        </div>
      )}
      <div className="detail-row">
        <span className="k">Observer</span>
        <span>{packet.observer_id && <ObserverBadge observerId={packet.observer_id} trustTier={packet.trust_tier} />}</span>
      </div>
      <div className="detail-row">
        <span className="k">Trust tier</span>
        <span>{trustTierInfo(packet.trust_tier).label}</span>
      </div>
      <div className="detail-row">
        <span className="k">Protocol</span>
        <span>{protocolLabel}</span>
      </div>

      {isTrace ? <ShowTraceLink packet={packet} /> : <div style={{ marginTop: 10, fontSize: 11, color: "var(--ink-muted)" }}>{provenance}</div>}

      <button className="replay-action-btn" onClick={() => replaySinglePacket(packet)}>
        ▶ Replay this packet's animation
      </button>

      {packet.summary && (
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <div className="k" style={{ color: "var(--ink-muted)", marginBottom: 2 }}>
            Summary
          </div>
          {packet.summary}
        </div>
      )}

      {/* Decoded payload, route_hops, and protocol_extra are each rendered
          only when present — route_hops/protocol_extra are MeshCore-
          relevant and routinely null on Meshtastic; never print "null". */}
      {packet.payload && (
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <div className="k" style={{ color: "var(--ink-muted)", marginBottom: 4 }}>
            Payload
          </div>
          <PayloadView payload={packet.payload} />
        </div>
      )}

      {packet.route_hops && packet.route_hops.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <div className="k" style={{ color: "var(--ink-muted)", marginBottom: 4 }}>
            Route hops
          </div>
          <PayloadView payload={packet.route_hops} />
        </div>
      )}

      {packet.protocol_extra && (
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <div className="k" style={{ color: "var(--ink-muted)", marginBottom: 4 }}>
            Protocol extra
          </div>
          <PayloadView payload={packet.protocol_extra} />
        </div>
      )}

      {/* Bytes: the raw MeshCore packet, field by field (the server computes it from the stored capture). */}
      {packet.hash && packet.protocol === "meshcore" && (
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <div className="k" style={{ color: "var(--ink-muted)", marginBottom: 4 }}>
            Bytes
          </div>
          <PacketBytesSection hash={packet.hash} routeHops={packet.route_hops?.length ?? 0} />
        </div>
      )}

      {packet.hash && (
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <div className="k" style={{ color: "var(--ink-muted)", marginBottom: 4 }}>
            Coverage — every observer that heard this packet
          </div>
          <PacketCoverageSection hash={packet.hash} />
        </div>
      )}
    </div>
  );
}
